import type { Product, ProductLeaf, PropertyValue } from "@decocms/apps-commerce/types";
import { mapProductToAnalyticsItem } from "@decocms/apps-commerce/utils/productToAnalyticsItem";
import Image from "~/components/ui/Image";
import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { SendEventOnClick } from "~/components/Analytics";
import DiscountBadge from "~/components/ui/DiscountBadge";
import VariantSelectorShelf from "~/components/product/VariantSelectorShelf";
import WishlistButton from "~/components/wishlist/WishlistButton";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";
import { useOffer } from "~/sdk/useOffer";
import { useSkuSelector } from "~/sdk/useSkuSelector";
import { useVariantPossibilities } from "~/sdk/useVariantPossiblities";
import { getProductImageAlt, getSelectedColor } from "~/utils/product";
import { sanitizeStoreUrl } from "~/utils/constants";
import ProductFlagImages from "./ProductFlagImages";
import SkuSelector from "./SkuSelector";
import type {
  Flag,
  IParams,
  Layout,
  ProductCardProps as Props,
} from "./types";

export type { Flag, IParams, Layout, Props };

function ProductFlags({
  properties,
  flags,
  listPrice,
  price,
  badgePosition,
}: {
  properties: PropertyValue[];
  flags: Flag[];
  listPrice?: number;
  price?: number;
  badgePosition?: string;
}) {
  const flagsToBeShown = flags?.filter((flag) => {
    if (properties?.some((property) => property.value === flag.title)) {
      return flag;
    }
  });

  const hasDiscount = listPrice && price && listPrice !== price;

  if (!flagsToBeShown.length && !hasDiscount) return null;

  const defaultBadgePosition = "left-2 top-1 lg:left-10 lg:top-5";
  const badgePositionClasses = badgePosition || defaultBadgePosition;

  return (
    <div
      className={`absolute flex flex-col gap-[5px] ${badgePositionClasses} lg:gap-[7px] z-10`}
    >
      <DiscountBadge listPrice={listPrice} price={price} />
      <ProductFlagImages flagsToBeShown={flagsToBeShown} />
    </div>
  );
}

function ProductCard({
  product,
  itemListName,
  hideButton,
  squareImage: _squareImage,
  layout,
  platform,
  hideDiscountBadge,
  badgePosition,
  preload,
  setSelectedItems,
  setProductAvailable,
  setProductParams,
}: Props) {
  const { selectedSku } = useSkuSelector();

  const [currentSku, setCurrentSku] = useState<string | null>(selectedSku.value);

  const variantsOptions = product.isVariantOf?.hasVariant ?? [];

  const availableVariant = variantsOptions.find((variant) =>
    (variant.offers?.offers?.[0]?.inventoryLevel?.value ?? 0) > 0
  );

  const initialProduct = availableVariant || product;

  const [selectedProduct, setSelectedProduct] = useState<ProductLeaf | Product>(
    initialProduct,
  );
  const [imageObjectFit, setImageObjectFit] = useState<"cover" | "contain">(
    "cover",
  );
  const [isCardVisible, setIsCardVisible] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);

  // `??` não cobre variante com `image: []` (array vazio, não nullish) — sem
  // checar length, cai nela e perde a foto mesmo com `product.image` válido.
  const currentImages = selectedProduct.image?.length
    ? selectedProduct.image
    : product.image?.length
    ? product.image
    : [];
  const {
    productID,
    offers,
    additionalProperty,
  } = selectedProduct;

  const id = `product-card-${productID}`;
  const hasVariant = product.isVariantOf?.hasVariant ?? [];
  const productGroupID = product.isVariantOf?.productGroupID;
  const [front, back] = currentImages;
  const { listPrice, price, installments, seller = "1" } =
    useOffer(offers);
  const possibilities = useVariantPossibilities(hasVariant, product);
  // Prioridade fixa (mesmo padrão de ProductVariantSelector.tsx) em vez de
  // "o primeiro grupo que aparecer no objeto" — a ordem de inserção de
  // `possibilities` depende da ordem dos specs de cada SKU vindos da VTEX,
  // que pode divergir entre a renderização do servidor e a do cliente e
  // causava mismatch de hidratação (grupo errado = lista de variantes
  // diferente).
  const variantGroup = possibilities["Cor"] ?? possibilities["Tamanho"] ??
    Object.values(possibilities)[0] ?? {};
  const variants = Object.entries(variantGroup);

  const l = layout;
  const align =
    !l?.basics?.contentAlignment || l?.basics?.contentAlignment == "Left"
      ? "left"
      : "center";

  const shouldShowFlags = additionalProperty &&
    l?.productFlags?.showFlags &&
    l?.productFlags?.flags?.length;

  const shouldShowDiscountBadge = !hideDiscountBadge;

  const defaultBadgePosition = "left-2 top-1 lg:left-10 lg:top-5";
  const badgePositionClasses = badgePosition || defaultBadgePosition;

  const productName = product.isVariantOf?.name;

  useEffect(() => {
    const nextSelectedProduct = product.isVariantOf?.hasVariant?.find(
      (variant) => variant.sku === currentSku,
    );
    setSelectedProduct(nextSelectedProduct ?? initialProduct);

    if (setSelectedItems) {
      const nextProduct = nextSelectedProduct ?? initialProduct;

      setSelectedItems((prev) => {
        const newState = prev.map((item) => {
          if (item.inProductGroupWithID === nextProduct.inProductGroupWithID) {
            return nextProduct;
          }

          return item;
        });

        return newState;
      });
    }
  }, [currentSku, initialProduct, product, setSelectedItems]);

  useEffect(() => {
    const node = imageContainerRef.current;
    if (!node || isCardVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsCardVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [isCardVisible]);

  const handleImageLoad = (e: React.ChangeEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    // Se a imagem for muito vertical (aspect ratio < 0.7), usa contain
    // Ajuste este valor conforme necessário para suas imagens
    if (aspectRatio < 0.7) {
      setImageObjectFit("contain");
    } else {
      setImageObjectFit("cover");
    }
  };

  return (
    <div
      id={id}
      className={`card card-sm center group w-[97%] sm:w-full mx-auto h-full justify-between ${
        align === "center" ? "text-center" : "text-start"
      } ${l?.onMouseOver?.showCardShadow ? "lg:hover:card-bordered" : ""}
        ${
        l?.onMouseOver?.card === "Move up"
          ? "duration-500 transition-translate ease-in-out lg:hover:-translate-y-2"
          : ""
      }
      `}
      data-deco="view-product"
    >
      <SendEventOnClick
        id={id}
        event={{
          name: "select_item" as const,
          params: {
            item_list_name: itemListName,
            items: [
              mapProductToAnalyticsItem({
                // `selectedProduct` pode ser um `ProductLeaf` (item de
                // `hasVariant`), que não carrega `isVariantOf` — usar ele
                // direto aqui zeraria `item_name` (cai no fallback `name`,
                // que é só o rótulo da variante, ex. "Rosa/M", duplicando
                // `item_variant`). Mantém `isVariantOf`/categoria/marca de
                // `product` (o produto "pai"), só troca a identidade do SKU
                // (`productID`/`url`/`name`) pela variante de fato exibida —
                // sem isso, o evento de analytics podia reportar um SKU
                // diferente do que o card mostra/linka de verdade (achado
                // real: card com estoque só na variante M vindo mostrado
                // como PP, por causa do fallback "primeiro em estoque" logo
                // acima).
                product: {
                  ...product,
                  productID,
                  url: selectedProduct.url,
                  name: selectedProduct.name,
                },
                price,
                listPrice,
              }),
            ],
          },
        }}
      />

      {/* Wishlist button */}
      <div
        className={`absolute top-2 z-10
          ${
          l?.elementsPositions?.favoriteIcon === "Top left"
            ? "left-2"
            : "right-2"
        }
          ${
          l?.onMouseOver?.showFavoriteIcon
            ? "lg:hidden lg:group-hover:block"
            : "lg:hidden"
        }
        `}
      >
        {platform === "vtex" && (
          <WishlistButton
            productGroupID={productGroupID}
            productID={productID}
          />
        )}
      </div>

      {/* Flags and Discount Badge */}
      {shouldShowDiscountBadge && (
        shouldShowFlags
          ? (
            <ProductFlags
              properties={additionalProperty}
              flags={l?.productFlags?.flags as Flag[]}
              listPrice={listPrice}
              price={price}
              badgePosition={badgePosition}
            />
          )
          : (
            /* Discount Badge - Show if there's a discount, even without flags */
            <div className={`absolute ${badgePositionClasses} z-10`}>
              <DiscountBadge listPrice={listPrice} price={price} />
            </div>
          )
      )}

      {/* Product Images */}

      <div className="relative h-full overflow-hidden" ref={imageContainerRef}>
        <a
          href={sanitizeStoreUrl(availableVariant?.url || product.url)}
          tabIndex={-1}
          className="group flex flex-col items-center p-0 h-full"
        >
          {front.url && (() => {
            const selectedColor = getSelectedColor(selectedProduct);

            return (
              <>
                <Image
                  loading={preload ? "eager" : "lazy"}
                  fetchPriority={preload ? "high" : "low"}
                  decoding="async"
                  className={`lg:group-hover:hidden w-full min-h-[230px] lg:w-full lg:max-w-[424px] lg:h-auto lg:aspect-[424/594] ${
                    imageObjectFit === "cover"
                      ? "object-cover"
                      : "object-contain"
                  }`}
                  src={front.url!}
                  width={424}
                  height={594}
                  alt={getProductImageAlt(productName, 0, selectedColor)}
                  onLoad={handleImageLoad}
                />
                {isCardVisible && (
                  <Image
                    loading="eager"
                    fetchPriority="low"
                    decoding="async"
                    className={`hidden lg:group-hover:flex w-full min-h-[230px] lg:w-full lg:max-w-[424px] lg:h-auto lg:aspect-[424/594] ${
                      imageObjectFit === "cover"
                        ? "object-cover"
                        : "object-contain"
                    }`}
                    src={back?.url ?? front.url!}
                    width={424}
                    height={594}
                    alt={getProductImageAlt(productName, 1, selectedColor)}
                  />
                )}
              </>
            );
          })()}
        </a>
        <div className="flex w-full items-center flex-col lg:group-hover:justify-end">
          <VariantSelectorShelf
            {...{
              product,
              listPrice: listPrice ?? 0,
              price: price ?? 0,
              seller,
              setCurrentSku: (sku: string | number) => setCurrentSku(String(sku)),
              hideButton,
              setProductAvailable,
              setProductParams,
            }}
          />
        </div>
      </div>
      <a
        href={sanitizeStoreUrl(availableVariant?.url || product.url)}
        tabIndex={-1}
        className="group flex flex-col items-center p-0"
      >
        {/* `h-8` reserva espaço fixo pra até 2 linhas de nome (pra alinhar o
            preço na mesma altura entre cards de uma mesma fileira, mesmo com
            nomes de tamanhos diferentes) — sem `flex flex-col justify-end`
            aqui, nome de 1 linha só fica ancorado no TOPO da caixa, sobrando
            ~17px de espaço em branco embaixo do nome e antes do preço (gap
            "muito grande" reportado pelo time). Ancorar embaixo resolve o gap
            sem mudar a altura total (preço continua começando na mesma
            posição Y de sempre — zero impacto no alinhamento entre cards). */}
        <p className="text-start mt-4 font-['Poppins'] not-italic font-normal text-xs leading-[15px] text-[#757575]
                lg:text-sm h-8 overflow-hidden w-full tracking-[0.06em] max-w-[424px] m-auto flex flex-col justify-end">
          {productName}
        </p>

        {/* Prices & Name */}
        <div className="flex-auto flex flex-col p-0 justify-start w-full">
          {/* SKU Selector */}
          {(!l?.elementsPositions?.skuSelector ||
            l?.elementsPositions?.skuSelector === "Top") && (
            <>
              {l?.hide?.skuSelector
                ? (
                  ""
                )
                : (
                  <ul
                    className={`hidden items-center gap-2 w-full overflow-auto p-3 ${
                      align === "center" ? "justify-center" : "justify-start"
                    } ${l?.onMouseOver?.showSkuSelector ? "lg:hidden" : ""}`}
                  >
                    <SkuSelector variants={variants} />
                  </ul>
                )}
            </>
          )}
          {l?.hide?.allPrices
            ? (
              ""
            )
            : (
              <div className="flex flex-col gap-0 font-['Poppins'] mt-1 mb-2 w-full max-w-[424px] m-auto">
                <div
                  className={`flex flex-col gap-0 ${
                    l?.basics?.oldPriceSize === "Normal"
                      ? "h-[43px]"
                      : "h-[30px]"
                  } ${align === "center" ? "justify-center" : "justify-start"}`}
                >
                  <div
                    className={`line-through not-italic font-normal text-[10px] leading-[15px] text-start uppercase text-[#757575] ${
                      l?.basics?.oldPriceSize === "Normal" ? "lg:text-xl" : ""
                    }`}
                  >
                    {(listPrice ?? 0) > price!
                      ? formatPrice(listPrice, offers?.priceCurrency)
                      : ""}
                  </div>
                  <div className="not-italic font-medium text-[15px] leading-[15px] text-start uppercase text-[#1A1A1A]">
                    {formatPrice(price, offers?.priceCurrency)}
                  </div>
                </div>
                {l?.hide?.installments || !installments
                  ? (
                    // Sem parcelamento na oferta (ex: SKU sem estoque, que a
                    // VTEX devolve sem `Installments`) o card imprimia um "ou"
                    // solto, sem valor nenhum depois.
                    ""
                  )
                  : (
                    <div
                      className={`text-xs truncate font-normal justify-start text-[#434343] ${
                        hideButton ? "hidden" : "flex"
                      }`}
                    >
                      ou {installments}
                    </div>
                  )}
              </div>
            )}
        </div>
      </a>
    </div>
  );
}

export default ProductCard;
